import { Box } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const animations = {
  fadeInUp,
  fadeInDown,
  fadeInLeft,
  fadeInRight,
  fadeIn,
  scaleIn,
};

const AnimatedContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    !['isVisible', 'animation', 'duration', 'delay'].includes(prop),
})(({ isVisible, animation = 'fadeInUp', duration = '0.6s', delay = '0s' }) => ({
  opacity: 0,
  ...(isVisible && {
    animation: `${animations[animation]} ${duration} ease-out ${delay} forwards`,
  }),
}));

export default function AnimatedBox({
  children,
  animation = 'fadeInUp',
  duration = '0.6s',
  delay = '0s',
  threshold = 0.1,
  triggerOnce = true,
  sx = {},
  ...props
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(entry.target);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, triggerOnce]);

  return (
    <AnimatedContainer
      ref={ref}
      isVisible={isVisible}
      animation={animation}
      duration={duration}
      delay={delay}
      sx={sx}
      {...props}
    >
      {children}
    </AnimatedContainer>
  );
}
